import {
    AUDIT_USER_REPLACEMENTS,
    CONSENT_EVENT_TYPES,
} from '/b2b/common/components/AuditEventLog/components/AuditEvent/constants';
import { headingFor, parseAdditional, prettyEventValue, prettyPath } from './helpers';

import { describeChange } from './describeChange';
import { makeResultsFormatter } from '../../helpers';
import { startCase } from 'lodash';
import { translate } from '/b2b/common/helpers/i18n';

export const formatConsentAuditEvent = (data = {}) => {
    const {
        additional,
        audit_event_type: auditEventType,
        audit_log_id: auditLogId,
        audit_time: auditTime,
        audit_user: rawUser,
        resource_id: auditResourceId,
        resource_type: auditResourceType,
    } = data;

    const base = {
        auditEventType,
        auditLogId,
        auditTime,
        auditUser: (AUDIT_USER_REPLACEMENTS && AUDIT_USER_REPLACEMENTS[rawUser]) || rawUser,
        auditResourceId,
        auditResourceType,
    };

    const add = parseAdditional(additional);
    const metadata = add?.metadata ?? {};

    switch (auditEventType) {
        case CONSENT_EVENT_TYPES.CONFIG_UPDATED: {
            const { path, changeType } = metadata;
            const heading = headingFor({ changeType });
            const name = translate('activityLog.events.cmp.configUpdated', {
                heading,
                user: base.auditUser,
            });
            const { label, scope, before, after } = describeChange(metadata);

            const description = scope
                ? translate('activityLog.events.cmp.scopedChangeDescription', {
                      path: label,
                      scope,
                      before,
                      after,
                  })
                : translate('activityLog.events.cmp.changeDescription', {
                      path: label,
                      before,
                      after,
                  });

            return {
                ...base,
                name,
                description,
                change: {
                    id: auditLogId,
                    path,
                    label,
                    scope,
                    before,
                    after,
                    changeType,
                },
            };
        }

        case CONSENT_EVENT_TYPES.CONFIG_PUBLISHED: {
            const changeIds = Array.isArray(metadata.changeIds) ? metadata.changeIds : [];
            const by = add?.publishedBy || add?.publisher || base.auditUser;
            const name =
                changeIds.length === 0
                    ? translate('activityLog.events.cmp.configPublished.text', { user: by })
                    : translate('activityLog.events.cmp.configPublished.changes', {
                          count: changeIds.length,
                          user: by,
                          smart_count: changeIds.length,
                      });
            return { ...base, name, changeIds };
        }

        case CONSENT_EVENT_TYPES.CONFIG_ROLLBACK: {
            const revision =
                metadata.newVersion ??
                metadata.toVersion ??
                metadata.toRevision ??
                metadata.revision;
            const descriptionParts = [];

            if (revision != null) {
                descriptionParts.push(
                    translate('activityLog.events.cmp.configRollback.toRevision', { revision })
                );
            }
            descriptionParts.push(
                translate('activityLog.events.cmp.configRollback.byUser', { user: base.auditUser })
            );

            return {
                ...base,
                name: translate('activityLog.events.cmp.configRollback'),
                description: descriptionParts.join(' • '),
                revision,
            };
        }

        case CONSENT_EVENT_TYPES.CONFIG_MSPA_ID_UPDATED: {
            const { previousMspaId, mspaId } = metadata;
            return {
                ...base,
                name: translate('activityLog.events.cmp.configMspaIdUpdated', {
                    user: base.auditUser,
                }),
                description:
                    previousMspaId == null
                        ? translate('activityLog.events.cmp.configMspaIdUpdated.set', { mspaId })
                        : translate('activityLog.events.cmp.configMspaIdUpdated.changed', {
                              mspaId,
                              previousMspaId,
                          }),
            };
        }

        case CONSENT_EVENT_TYPES.RULE_UPDATED: {
            const { path, before, after, ruleName, storeType, classification, changeType } =
                metadata || {};
            const description = translate('activityLog.events.cmp.ruleChangeDescription', {
                classification,
                storeType: startCase(storeType),
                path: prettyPath(path, changeType),
                before: prettyEventValue(before),
                after: prettyEventValue(after),
            });
            return {
                ...base,
                name: translate('activityLog.events.cmp.ruleUpdated', {
                    ruleName,
                    user: base.auditUser,
                }),
                description,
            };
        }

        case CONSENT_EVENT_TYPES.URLSCAN_CREATED:
        case CONSENT_EVENT_TYPES.SITEMAP_URL_CREATED: {
            const { url } = metadata;
            const nameKey =
                auditEventType === CONSENT_EVENT_TYPES.URLSCAN_CREATED
                    ? 'activityLog.events.cmp.urlScanCreated'
                    : 'activityLog.events.cmp.sitemapUrlCreated';

            const descriptionKey =
                auditEventType === CONSENT_EVENT_TYPES.URLSCAN_CREATED
                    ? 'activityLog.events.cmp.urlScanDescription'
                    : 'activityLog.events.cmp.sitemapUrlDescription';

            return {
                ...base,
                name: translate(nameKey, { user: base.auditUser }),
                description: translate(descriptionKey, { url }),
            };
        }

        case CONSENT_EVENT_TYPES.URLSCAN_DELETED:
        case CONSENT_EVENT_TYPES.SITEMAP_URL_DELETED: {
            const { url } = metadata;
            const nameKey =
                auditEventType === CONSENT_EVENT_TYPES.URLSCAN_DELETED
                    ? 'activityLog.events.cmp.urlScanDeleted'
                    : 'activityLog.events.cmp.sitemapUrlDeleted';

            const descriptionKey =
                auditEventType === CONSENT_EVENT_TYPES.URLSCAN_DELETED
                    ? 'activityLog.events.cmp.urlScanDescription'
                    : 'activityLog.events.cmp.sitemapUrlDescription';

            return {
                ...base,
                name: translate(nameKey, { user: base.auditUser }),
                description: translate(descriptionKey, { url }),
            };
        }

        case CONSENT_EVENT_TYPES.URLSCAN_UPDATED:
        case CONSENT_EVENT_TYPES.SITEMAP_URL_UPDATED: {
            const { url } = metadata;
            const nameKey =
                auditEventType === CONSENT_EVENT_TYPES.URLSCAN_UPDATED
                    ? 'activityLog.events.cmp.urlScanUpdated'
                    : 'activityLog.events.cmp.sitemapUrlUpdated';

            const descriptionKey =
                auditEventType === CONSENT_EVENT_TYPES.URLSCAN_UPDATED
                    ? 'activityLog.events.cmp.urlScanDescription'
                    : 'activityLog.events.cmp.sitemapUrlDescription';

            return {
                ...base,
                name: translate(nameKey, { user: base.auditUser }),
                description: translate(descriptionKey, { url }),
            };
        }

        case CONSENT_EVENT_TYPES.RULE_DELETED: {
            const { ruleName } = metadata;
            return {
                ...base,
                name: translate('activityLog.events.cmp.ruleDeleted', {
                    ruleName,
                    user: base.auditUser,
                }),
            };
        }

        case CONSENT_EVENT_TYPES.RULE_CREATED: {
            const { ruleName, storeType, classification } = metadata;
            return {
                ...base,
                name: translate('activityLog.events.cmp.ruleCreated', {
                    ruleName,
                    user: base.auditUser,
                }),
                description: translate('activityLog.events.cmp.ruleCreated.description', {
                    classification: startCase(classification),
                    storeType: startCase(storeType),
                }),
            };
        }

        default: {
            const eventTypeKey = `activityLog.events.${auditEventType}`;
            return {
                ...base,
                name: translate('activityLog.events.cmp.generic', {
                    eventType: translate(eventTypeKey),
                    user: base.auditUser,
                }),
            };
        }
    }
};

export const formatConsentAuditEventResults = makeResultsFormatter(formatConsentAuditEvent);
